import React, { useState } from 'react';
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  Fab,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import BrushIcon from '@mui/icons-material/Brush';
import PaletteIcon from '@mui/icons-material/Palette';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CollectionsIcon from '@mui/icons-material/Collections';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Spike 1 §1.3 — Mobile shell paradigm. Bottom-tab navigation with drawer
 * overlays for short interactions.
 *
 * Five tabs at the bottom (Canvas / Swatches / Action / Agent / Gallery).
 * Active tab takes the full viewport. AgentBar is intentionally NOT a tab —
 * it persists as a slim collapsed strip above the bottom tabs so voice / chat
 * are always one tap away regardless of the active tab.
 *
 * This is a **scaffold**: it exposes a slot per tab (`canvasSlot`, `swatchesSlot`,
 * etc.) so the per-lab orchestrator (`ImageLab.tsx`, `WriterLab.tsx`) can
 * decide what to render in each. The shell itself owns the active-tab state,
 * the bottom-nav UI, and the drawer state for the swatch / op pickers.
 *
 * Drawer overlays (per spike §1.3) — v3.0.1 polish shipped 2026-05-26:
 *   - Swatch picker → bottom sheet, 80% viewport height. Opens from a FAB on
 *     the Canvas tab so the user can pick a swatch without leaving canvas.
 *   - Op picker    → top sheet, 60% viewport height. Same Canvas-tab pattern.
 *   - Model picker → owned by AgentBar's existing ProviderModelSelect; the
 *     shell doesn't host it.
 *
 * Drawers are mounted by the shell, but the SLOT CONTENT is whatever the lab
 * orchestrator passes via `swatchesSlot` / `actionSlot`. Because MUI Drawer
 * unmounts its content when closed (no `keepMounted`), the slot is rendered
 * in exactly one place at a time — either the tab view OR the drawer — so
 * components inside the slot don't double-mount.
 *
 * Desktop is unaffected: this whole shell only mounts when `useMobileShell()`
 * returns `isMobileShell=true` (i.e. viewport < md, 900px). The desktop rails
 * in `ImageLab.tsx` / `WriterLab.tsx` continue to render unchanged when the
 * viewport widens past md.
 */

export type MobileShellTab = 'canvas' | 'swatches' | 'action' | 'agent' | 'gallery';

interface Props {
  /** The lab-area mounted as the canvas tab. Filled by the per-lab orchestrator. */
  canvasSlot: React.ReactNode;
  /**
   * The swatches view (left rail on desktop) shown in the swatches tab.
   * Pass null/undefined for labs that have no swatches (e.g. v3.1 Mood lab).
   */
  swatchesSlot?: React.ReactNode;
  /** The action panel (right rail on desktop) — Operations + Agents tabs. */
  actionSlot?: React.ReactNode;
  /** The Agent Bar persists above the bottom tabs. */
  agentBarSlot?: React.ReactNode;
  /** Gallery view shown in the gallery tab. */
  gallerySlot?: React.ReactNode;
  /** Header title — the lab/studio name. */
  title?: string;
  /**
   * Initial tab. Defaults to `canvas` — the most common entry point. Consumers
   * pass `gallery` when routing in from a consumption surface.
   */
  initialTab?: MobileShellTab;
}

const TAB_HEIGHT = 56;
const AGENT_STRIP_HEIGHT = 48;

export const MobileShell: React.FC<Props> = ({
  canvasSlot,
  swatchesSlot,
  actionSlot,
  agentBarSlot,
  gallerySlot,
  title,
  initialTab = 'canvas',
}) => {
  const [tab, setTab] = useState<MobileShellTab>(initialTab);
  const [headerDrawerOpen, setHeaderDrawerOpen] = useState(false);
  // v3.0.1 polish — drawer overlays. Only available from the Canvas tab so
  // they don't compete with the bottom-tab navigation. Each drawer hosts the
  // same slot content (`swatchesSlot` / `actionSlot`) the corresponding tab
  // would show, with MUI Drawer's default unmount-on-close keeping the slot
  // rendered in exactly one place at a time.
  const [swatchDrawerOpen, setSwatchDrawerOpen] = useState(false);
  const [opDrawerOpen, setOpDrawerOpen] = useState(false);
  // Closing a drawer when navigating away from Canvas prevents the drawer
  // from "lingering" if the user taps a tab while the drawer is animating
  // closed. Without this the bottom-tab transition feels glitchy.
  const handleTabChange = (next: MobileShellTab) => {
    setTab(next);
    if (next !== 'canvas') {
      setSwatchDrawerOpen(false);
      setOpDrawerOpen(false);
    }
  };
  const canvasFabsVisible = tab === 'canvas';

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Slim app bar — title + a hamburger that hosts secondary actions
          (theme picker, account, etc.) so the bottom tabs stay reserved
          for primary navigation. */}
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <IconButton
            edge="start"
            size="small"
            onClick={() => setHeaderDrawerOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
            {title ?? 'Coral Creator'}
          </Typography>
          <Box sx={{ width: 32 }} />
        </Toolbar>
      </AppBar>

      {/* Center: the active tab slot fills everything between the AppBar
          and the AgentBar + bottom tabs. Note: when one of the drawer
          overlays is open, the corresponding slot is hosted INSIDE the
          drawer rather than its tab, so we avoid double-mounting it here. */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          // Reserve space for the persistent AgentBar strip + bottom tabs.
          pb: `${TAB_HEIGHT + AGENT_STRIP_HEIGHT}px`,
          position: 'relative', // anchor for the canvas-tab FABs
        }}
        aria-label={`${tab} view`}
      >
        {tab === 'canvas' && canvasSlot}
        {tab === 'swatches' && !swatchDrawerOpen && (swatchesSlot ?? <EmptyTabMessage label="No swatches in this lab." />)}
        {tab === 'action' && !opDrawerOpen && (actionSlot ?? <EmptyTabMessage label="No operations available." />)}
        {tab === 'agent' && (agentBarSlot ?? <EmptyTabMessage label="Agent bar is unavailable." />)}
        {tab === 'gallery' && (gallerySlot ?? <EmptyTabMessage label="No gallery view." />)}

        {/* v3.0.1 polish — FAB triggers for the Swatch / Op drawers. Stacked
            in the bottom-right of the Canvas viewport, above the AgentBar
            strip + bottom tabs. Visible only on Canvas tab AND only when the
            corresponding slot is provided (no FAB for a lab with no
            swatches). */}
        {canvasFabsVisible && (swatchesSlot || actionSlot) && (
          <Stack
            spacing={1}
            sx={{
              position: 'fixed',
              right: 16,
              bottom: TAB_HEIGHT + AGENT_STRIP_HEIGHT + 16,
              zIndex: theme => theme.zIndex.speedDial,
            }}
          >
            {actionSlot && (
              <Tooltip title="Operations" placement="left">
                <Fab
                  size="small"
                  color="primary"
                  aria-label="Open operations picker"
                  onClick={() => setOpDrawerOpen(true)}
                >
                  <AutoFixHighIcon />
                </Fab>
              </Tooltip>
            )}
            {swatchesSlot && (
              <Tooltip title="Swatches" placement="left">
                <Fab
                  size="small"
                  color="secondary"
                  aria-label="Open swatch picker"
                  onClick={() => setSwatchDrawerOpen(true)}
                >
                  <PaletteIcon />
                </Fab>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>

      {/* AgentBar strip — slim, always-visible above the bottom tabs.
          When the user taps the strip it expands; v3.0 scaffold keeps the
          interaction in the existing AgentBar component (which already
          handles its own expand/collapse), so we just give it real estate. */}
      {agentBarSlot && tab !== 'agent' && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: TAB_HEIGHT,
            height: AGENT_STRIP_HEIGHT,
            bgcolor: 'background.paper',
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            px: 1,
            // Truncate to one line so the strip stays compact when collapsed.
            overflow: 'hidden',
          }}
        >
          {agentBarSlot}
        </Box>
      )}

      {/* Bottom tabs — the primary navigation. */}
      <BottomNavigation
        value={tab}
        onChange={(_, next: MobileShellTab) => handleTabChange(next)}
        showLabels={false}
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          height: TAB_HEIGHT,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <BottomNavigationAction
          value="canvas"
          icon={<BrushIcon />}
          label="Canvas"
          aria-label="Canvas tab"
        />
        <BottomNavigationAction
          value="swatches"
          icon={<PaletteIcon />}
          label="Swatches"
          aria-label="Swatches tab"
        />
        <BottomNavigationAction
          value="action"
          icon={<AutoFixHighIcon />}
          label="Action"
          aria-label="Action tab"
        />
        <BottomNavigationAction
          value="agent"
          icon={<SmartToyOutlinedIcon />}
          label="Agent"
          aria-label="Agent tab"
        />
        <BottomNavigationAction
          value="gallery"
          icon={<CollectionsIcon />}
          label="Gallery"
          aria-label="Gallery tab"
        />
      </BottomNavigation>

      {/* Header hamburger — secondary actions slide in from the left. */}
      <Drawer
        anchor="left"
        open={headerDrawerOpen}
        onClose={() => setHeaderDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 260, p: 2 }} role="navigation">
          <Typography variant="overline" color="text.secondary">
            Settings
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            Theme picker, account, and lab switcher land here in v3.0.1.
          </Typography>
        </Box>
      </Drawer>

      {/* v3.0.1 polish — Swatch picker drawer. Bottom-anchored modal sheet
          at 80% viewport height per spike §1.3. MUI's default keepMounted is
          false so the slot content unmounts on close (no double-render with
          the swatches tab). Header carries a close affordance for screen-
          reader users who don't trigger the backdrop click. */}
      {swatchesSlot && (
        <Drawer
          anchor="bottom"
          open={swatchDrawerOpen}
          onClose={() => setSwatchDrawerOpen(false)}
          PaperProps={{
            sx: {
              height: '80vh',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              display: 'flex',
              flexDirection: 'column',
            },
            'aria-label': 'Swatch picker',
          }}
        >
          <DrawerHeader title="Swatches" onClose={() => setSwatchDrawerOpen(false)} />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {swatchesSlot}
          </Box>
        </Drawer>
      )}

      {/* v3.0.1 polish — Op picker drawer. Top-anchored modal sheet at 60%
          viewport height per spike §1.3. Same slot-once-only rule as the
          swatch drawer (action tab body unmounts when this drawer is open). */}
      {actionSlot && (
        <Drawer
          anchor="top"
          open={opDrawerOpen}
          onClose={() => setOpDrawerOpen(false)}
          PaperProps={{
            sx: {
              height: '60vh',
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              display: 'flex',
              flexDirection: 'column',
            },
            'aria-label': 'Operations picker',
          }}
        >
          <DrawerHeader title="Operations" onClose={() => setOpDrawerOpen(false)} />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {actionSlot}
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

const DrawerHeader: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 2,
      py: 1,
      borderBottom: theme => `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    }}
  >
    <Typography variant="subtitle2" fontWeight={600}>
      {title}
    </Typography>
    <IconButton size="small" onClick={onClose} aria-label={`Close ${title.toLowerCase()}`}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

const EmptyTabMessage: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ p: 4, color: 'text.secondary', textAlign: 'center' }}>
    <Typography variant="body2">{label}</Typography>
  </Box>
);
